import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Tag, CheckCircle2, X, MapPin, User, Gift, Clock, Info, Truck } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useFmt } from "../context/CurrencyContext";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const DAY_NAMES = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

/** Returns the upcoming list of valid delivery dates that respect the
 * Jamaica-local cut-off rule:
 *   • Orders placed before Wed 12:00 PM JA   → eligible for Thursday
 *   • Orders placed before Fri 12:00 PM JA   → eligible for Saturday
 *   • Orders placed after the cut-off slide to the next available date.
 *
 * `days` is the admin-configured list of allowed weekday names (e.g.
 * ["Thursday", "Saturday"]) — we only emit dates whose weekday is in that
 * list. We use UTC math + a fixed −05:00 offset (Jamaica has no DST).
 */
function nextDeliveryDates(days = [], count = 6) {
  const JM_OFFSET_MIN = -5 * 60; // Jamaica is UTC−05, no DST
  const now = new Date();
  const localMinutes = now.getUTCMinutes() + now.getTimezoneOffset() * 0; // not used
  // Build a "now in Jamaica" Date by adjusting via UTC + offset
  const jmNow = new Date(now.getTime() + (JM_OFFSET_MIN - now.getTimezoneOffset() * -1) * 60000);
  // Simpler: get JM hour/day directly using toLocaleString
  const jm = new Date(now.toLocaleString("en-US", { timeZone: "America/Jamaica" }));
  const out = [];
  for (let i = 0; i <= 30 && out.length < count; i++) {
    const d = new Date(jm);
    d.setDate(jm.getDate() + i);
    const dayName = DAY_NAMES[d.getDay()];
    if (!days.includes(dayName)) continue;
    // Apply cut-off rules: for the FIRST candidate, ensure cut-off hasn't passed
    if (i === 0) continue; // never deliver same-day
    if (dayName === "Thursday") {
      // Wednesday 12:00 PM JM cut-off
      const cutoff = new Date(d);
      cutoff.setDate(d.getDate() - 1); // Wednesday
      cutoff.setHours(12, 0, 0, 0);
      if (jm > cutoff) continue;
    } else if (dayName === "Saturday") {
      const cutoff = new Date(d);
      cutoff.setDate(d.getDate() - 1); // Friday
      cutoff.setHours(12, 0, 0, 0);
      if (jm > cutoff) continue;
    }
    out.push({
      date: d.toISOString().slice(0, 10),
      dayName,
      label: `${dayName}, ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`,
    });
    void jmNow; void localMinutes;
  }
  return out;
}

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [settings, setSettings] = useState({ zones: [], delivery_days: [] });
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    address_line: "", parish: "", delivery_day: "", notes: "", delivery_notes: "",
    fulfillment_method: "delivery",  // "delivery" | "pickup"
    is_gift: false,
    recipient_name: "", recipient_phone: "", recipient_email: "", recipient_address: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const fmtJMD = useFmt();
  const [error, setError] = useState("");

  // Coupon state
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);

  // Address book
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");

  useEffect(() => {
    api.get("/delivery/settings").then((r) => {
      setSettings(r.data);
      if (r.data.zones?.[0]) setForm((f) => ({ ...f, parish: r.data.zones[0].parish }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && user.role === "customer") {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || user.name || "",
        customer_email: f.customer_email || user.email || "",
      }));
      api.get("/account/addresses").then((r) => {
        setSavedAddresses(r.data);
        const def = r.data.find((a) => a.is_default) || r.data[0];
        if (def) { applyAddress(def); setSelectedAddressId(def.id); }
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [user]);

  const applyAddress = (a) => {
    setForm((f) => ({
      ...f,
      customer_name: a.name || f.customer_name,
      customer_phone: a.phone || f.customer_phone,
      address_line: a.address_line,
      parish: a.parish,
    }));
  };

  const zone = settings.zones.find((z) => z.parish === form.parish);
  const isPickup = form.fulfillment_method === "pickup";
  // Delivery fees are confirmed by the admin AFTER the order is placed —
  // customers no longer see an upfront fee at checkout. The `zone.fee` is
  // still surfaced to admins as a suggested starting point on the order row.
  const deliveryFee = 0;
  const suggestedFee = zone ? zone.fee : 0;
  const discount = coupon?.discount || 0;
  const total = Math.max(0, subtotal + deliveryFee - discount);
  const dates = nextDeliveryDates(settings.delivery_days);

  useEffect(() => {
    if (!form.delivery_day && dates.length > 0) {
      setForm((f) => ({ ...f, delivery_day: dates[0].date }));
    }
    // eslint-disable-next-line
  }, [settings]);

  const applyCoupon = async () => {
    setCouponError("");
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    try {
      const { data } = await api.post("/coupons/validate",
        { code: couponInput.trim().toUpperCase(), subtotal });
      setCoupon(data);
      toast.success(`${data.code} applied — you saved ${fmtJMD(data.discount)}`);
    } catch (e) {
      setCouponError(formatApiError(e.response?.data?.detail) || "Invalid coupon");
      setCoupon(null);
    } finally { setCouponLoading(false); }
  };
  const clearCoupon = () => { setCoupon(null); setCouponInput(""); setCouponError(""); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (items.length === 0) { setError("Cart is empty"); return; }
    // Enforce gift-order required fields client-side
    if (form.is_gift && !isPickup) {
      if (!form.recipient_name.trim() || !form.recipient_phone.trim() || !form.recipient_address.trim()) {
        setError("Recipient name, phone, and address are required for gift orders.");
        return;
      }
    }
    if (form.is_gift && isPickup) {
      if (!form.recipient_name.trim() || !form.recipient_phone.trim()) {
        setError("Recipient name and phone are required for gift pickup orders.");
        return;
      }
    }
    setSubmitting(true);
    try {
      // For gift orders, the recipient's address IS the delivery address —
      // it takes precedence over the buyer's own address_line so drivers,
      // labels and the manifest all point to where the pops should go.
      let shipTo;
      if (isPickup) {
        // Pickup orders don't need a street address at all.
        shipTo = "Pickup — Greater Portmore, St. Catherine";
      } else if (form.is_gift) {
        shipTo = form.recipient_address.trim();
      } else {
        shipTo = form.address_line.trim();
      }
      const payload = {
        ...form,
        address_line: shipTo,
        parish: isPickup ? "Saint Catherine" : form.parish,
        delivery_day: isPickup ? "" : form.delivery_day,
        delivery_fee: deliveryFee,  // always 0 at checkout; admin sets later
        coupon_code: coupon?.code || null,
        discount_amount: discount,
        items: items.map((it) => ({
          type: it.type,
          ref_id: it.ref_id || null,
          name: it.name,
          price: it.price,
          quantity: it.quantity,
          custom_flavours: it.custom_flavours || [],
        })),
        subtotal,
        total,
      };
      const { data } = await api.post("/orders", payload);
      clear();
      toast.success("Order placed!");
      navigate(`/order/${data.id}`, { state: { order: data } });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0)
    return <div className="py-24 text-center text-zinc-500">Your cart is empty.</div>;

  // Require login for checkout (#18). Cart is preserved (lives in localStorage).
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return (
      <div className="max-w-2xl mx-auto px-5 sm:px-6 py-16 sm:py-24" data-testid="checkout-login-gate">
        <div className="rounded-2xl border border-zinc-200 bg-white p-6 sm:p-10 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-zinc-900 text-white inline-flex items-center justify-center mb-5">
            <User size={20} />
          </div>
          <div className="overline text-zinc-500 mb-2">Almost there</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
            Sign in to complete your order
          </h1>
          <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
            We use your account to send order updates and let you track every box you order.
            Your cart will be waiting for you after sign-in.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={`/login?mode=register&next=${next}`} className="btn-primary inline-flex justify-center" data-testid="checkout-register-cta">
              Create an account
            </Link>
            <Link to={`/login?next=${next}`} className="btn-ghost inline-flex justify-center" data-testid="checkout-login-cta">
              I already have an account
            </Link>
          </div>
          <p className="mt-6 text-[11px] text-zinc-400">Takes under a minute · cart preserved automatically</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-6 md:px-12 py-12 sm:py-16" data-testid="checkout-page">
      <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-8 sm:mb-10">Checkout</h1>
      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
        <div className="lg:col-span-2 space-y-6">
          <section className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6">
            <div className="overline text-zinc-500 mb-4">Your contact</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Full name" required value={form.customer_name}
                     onChange={(v) => setForm({ ...form, customer_name: v })} testid="checkout-name" />
              <Input label="Email" required type="email" value={form.customer_email}
                     onChange={(v) => setForm({ ...form, customer_email: v })} testid="checkout-email" />
              <Input label="Phone / WhatsApp" required value={form.customer_phone}
                     onChange={(v) => setForm({ ...form, customer_phone: v })} testid="checkout-phone" />
            </div>
          </section>

          {/* Recipient (gift order) */}
          <section className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6" data-testid="recipient-section">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div className="overline text-zinc-500 inline-flex items-center gap-1.5">
                <Gift size={11} /> Recipient information
              </div>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <span className="text-zinc-600">Is this order for someone else?</span>
                <span className="inline-flex rounded-full bg-zinc-100 p-0.5">
                  <button type="button"
                    onClick={() => setForm({ ...form, is_gift: false })}
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${!form.is_gift ? "bg-white shadow text-zinc-900" : "text-zinc-500"}`}
                    data-testid="gift-toggle-no">No</button>
                  <button type="button"
                    onClick={() => setForm({ ...form, is_gift: true })}
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${form.is_gift ? "bg-[#F74B64] text-white" : "text-zinc-500"}`}
                    data-testid="gift-toggle-yes">Yes</button>
                </span>
              </label>
            </div>
            {form.is_gift && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="recipient-fields">
                <Input label="Recipient full name" required value={form.recipient_name}
                       onChange={(v) => setForm({ ...form, recipient_name: v })} testid="recipient-name" />
                <Input label="Recipient phone / WhatsApp" required value={form.recipient_phone}
                       onChange={(v) => setForm({ ...form, recipient_phone: v })} testid="recipient-phone" />
                <Input label="Recipient email (optional)" type="email" value={form.recipient_email}
                       onChange={(v) => setForm({ ...form, recipient_email: v })} testid="recipient-email" />
                <div className="md:col-span-2">
                  <label className="text-xs font-semibold text-zinc-600 block mb-1">
                    Recipient delivery address or landmark <span className="text-[#F74B64]">*</span>
                  </label>
                  <textarea rows={2} required={form.is_gift} value={form.recipient_address}
                            onChange={(e) => setForm({ ...form, recipient_address: e.target.value })}
                            placeholder="e.g. 12 Old Hope Rd, Kingston 5 / Margaritaville Ocho Rios"
                            className="w-full border border-zinc-200 rounded-lg px-3 py-2"
                            data-testid="recipient-address" />
                </div>
              </div>
            )}
            {!form.is_gift && (
              <p className="text-xs text-zinc-500">
                Toggle <strong>Yes</strong> if you’re sending these pops to someone else — birthday gift, employee
                appreciation, school treat, etc.
              </p>
            )}
          </section>

          <section className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6">
            <div className="overline text-zinc-500 mb-3 inline-flex items-center gap-1.5">
              <Truck size={11} /> How would you like to receive your order?
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button type="button"
                      onClick={() => setForm({ ...form, fulfillment_method: "delivery" })}
                      className={`text-left rounded-xl border-2 p-4 transition ${
                        form.fulfillment_method === "delivery"
                          ? "border-[#F74B64] bg-[#F74B64]/5"
                          : "border-zinc-200 hover:border-zinc-300"
                      }`}
                      data-testid="fulfillment-delivery">
                <div className="inline-flex items-center gap-2 font-semibold text-sm">
                  <Truck size={14} className="text-[#F74B64]" /> Delivery to my address
                </div>
                <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
                  Kingston &amp; St. Catherine central areas. Delivery fee confirmed by our team.
                </p>
              </button>
              <button type="button"
                      onClick={() => setForm({ ...form, fulfillment_method: "pickup" })}
                      className={`text-left rounded-xl border-2 p-4 transition ${
                        form.fulfillment_method === "pickup"
                          ? "border-[#009A49] bg-[#009A49]/5"
                          : "border-zinc-200 hover:border-zinc-300"
                      }`}
                      data-testid="fulfillment-pickup">
                <div className="inline-flex items-center gap-2 font-semibold text-sm">
                  <MapPin size={14} className="text-[#009A49]" /> Pickup at our office
                </div>
                <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
                  Greater Portmore, St. Catherine. No delivery fee — we&rsquo;ll confirm your pickup day when we process the order.
                </p>
              </button>
            </div>
          </section>

          <section className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="overline text-zinc-500 inline-flex items-center gap-1.5">
                <MapPin size={11} /> {isPickup ? "Pickup" : "Delivery"}
              </div>
              {savedAddresses.length > 0 && !form.is_gift && !isPickup && (
                <select value={selectedAddressId} onChange={(e) => {
                  setSelectedAddressId(e.target.value);
                  const a = savedAddresses.find((x) => x.id === e.target.value);
                  if (a) applyAddress(a);
                }}
                        className="text-xs border border-zinc-200 rounded-lg px-2 py-1"
                        data-testid="checkout-saved-addr">
                  <option value="">Use new address</option>
                  {savedAddresses.map((a) => (
                    <option key={a.id} value={a.id}>{a.label} · {a.parish}</option>
                  ))}
                </select>
              )}
            </div>

            {isPickup ? (
              <div className="rounded-xl bg-[#009A49]/10 border border-[#009A49]/30 p-4 text-sm leading-relaxed"
                   data-testid="pickup-notice">
                <div className="inline-flex items-center gap-1.5 font-semibold text-[#046531] mb-1">
                  <MapPin size={13} /> Pick up from our office
                </div>
                <div className="text-zinc-700 text-xs">
                  <strong>Fruit Blossoms · Greater Portmore, St. Catherine.</strong> Our team will contact you with
                  the exact <strong>pickup day &amp; time</strong> once we begin processing your order. No
                  delivery fee applies.
                </div>
              </div>
            ) : form.is_gift ? (
              // Gift order: the recipient's address IS the delivery address.
              // We only need the parish (fee/zone) and delivery day from this
              // section — the street address field is hidden to avoid confusion.
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 sm:p-4 text-sm text-amber-900 mb-4"
                   data-testid="gift-address-notice">
                <div className="inline-flex items-center gap-1.5 font-semibold mb-1">
                  <Gift size={13} /> Delivering to your recipient
                </div>
                <div className="text-xs text-amber-800 leading-relaxed">
                  We'll deliver to <strong>{form.recipient_name || "your recipient"}</strong> at{" "}
                  <em>{form.recipient_address || "the recipient address above"}</em>.
                  Only the parish and delivery day below are needed from you.
                </div>
              </div>
            ) : (
              <Input label="Street address" required value={form.address_line}
                     onChange={(v) => setForm({ ...form, address_line: v })} testid="checkout-address" />
            )}

            {!isPickup && (
              <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${form.is_gift ? "" : "mt-4"}`}>
                <div>
                  <label className="text-xs font-semibold text-zinc-600 block mb-1">
                    {form.is_gift ? "Recipient parish" : "Parish"}
                  </label>
                  <select value={form.parish} onChange={(e) => setForm({ ...form, parish: e.target.value })}
                          className="w-full border border-zinc-200 rounded-lg px-3 py-2" data-testid="checkout-parish">
                    {settings.zones.map((z) => (
                      <option key={z.parish} value={z.parish}>{z.parish}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-zinc-600 block mb-1">Delivery day</label>
                  <select value={form.delivery_day} onChange={(e) => setForm({ ...form, delivery_day: e.target.value })}
                          className="w-full border border-zinc-200 rounded-lg px-3 py-2" data-testid="checkout-day">
                    {dates.map((d) => (
                      <option key={d.date} value={d.date}>{d.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Delivery rules + area information */}
            <div className="mt-4 rounded-xl bg-zinc-50 border border-zinc-200 p-3 sm:p-4 text-xs text-zinc-700 leading-relaxed space-y-2">
              <div className="flex items-start gap-2">
                <Clock size={13} className="mt-0.5 flex-shrink-0 text-[#FFA62B]" />
                <div>
                  <strong className="text-zinc-900">Delivery schedule.</strong> Delivery Days are Thursday and Saturday.
                  Orders placed before <strong>Wednesday 12:00&nbsp;PM</strong> are scheduled for Thursday delivery.
                  Orders placed before <strong>Friday 12:00&nbsp;PM</strong> are scheduled for Saturday delivery. Orders
                  placed after the cut-off move to the next available delivery day. <em>(Times in Jamaica local time.)</em>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Info size={13} className="mt-0.5 flex-shrink-0 text-[#009A49]" />
                <div>
                  <strong className="text-zinc-900">Delivery area.</strong> Delivery is available to central areas and
                  landmarks within Kingston and St. Catherine, including Half Way Tree, Liguanea, Cross Roads, Duhaney Park,
                  Papine, and other approved central locations. Selected pickup/delivery points may also include
                  Margaritaville Ocho Rios, Artisan Village, and Falmouth. Delivery fees may be confirmed after the order is reviewed.
                </div>
              </div>
            </div>

            <div className="mt-4">
              <label className="text-xs font-semibold text-zinc-600 block mb-1">Order notes (optional)</label>
              <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        placeholder="Anything the team should know about your order"
                        className="w-full border border-zinc-200 rounded-lg px-3 py-2" data-testid="checkout-notes" />
            </div>
            <div className="mt-4">
              <label className="text-xs font-semibold text-zinc-600 block mb-1">Delivery instructions (optional)</label>
              <textarea rows={2} value={form.delivery_notes}
                        onChange={(e) => setForm({ ...form, delivery_notes: e.target.value })}
                        placeholder="Gate code, unit number, allergies, leave with security…"
                        className="w-full border border-zinc-200 rounded-lg px-3 py-2"
                        data-testid="checkout-delivery-notes" />
            </div>
          </section>

          <section className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6">
            <div className="overline text-zinc-500 mb-4">Payment</div>
            <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-5 text-sm text-zinc-600">
              <div className="font-semibold text-zinc-900 mb-1">Payment confirmation by our team</div>
              Orders are placed as <strong>pending</strong>. Our team reviews your delivery location, confirms the
              fee if applicable, and sends you payment instructions (bank transfer / WhatsApp). The live payment
              gateway will be wired here once activated.
            </div>
          </section>

          {error && <div className="text-sm text-red-600" data-testid="checkout-error">{error}</div>}
        </div>

        <div>
          <div className="lg:sticky lg:top-24 rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6">
            <div className="overline text-zinc-500 mb-3">Summary</div>
            <div className="space-y-2 text-sm mb-4 max-h-60 overflow-y-auto pr-2">
              {items.map((it) => (
                <div key={it.key} className="flex justify-between">
                  <span className="truncate pr-2">{it.quantity}× {it.name}</span>
                  <span className="font-semibold">{fmtJMD(it.price * it.quantity)}</span>
                </div>
              ))}
            </div>

            {/* Coupon */}
            <div className="border-t border-zinc-200 pt-4">
              {coupon ? (
                <div className="flex items-center justify-between gap-2 mb-3 p-2.5 rounded-lg bg-green-50 border border-green-200">
                  <div className="flex items-center gap-2 text-sm text-green-700 min-w-0">
                    <CheckCircle2 size={14} className="flex-shrink-0" />
                    <span className="font-semibold truncate">{coupon.code}</span>
                    <span className="text-xs">-{fmtJMD(coupon.discount)}</span>
                  </div>
                  <button type="button" onClick={clearCoupon}
                          className="p-1 hover:bg-green-100 rounded" data-testid="clear-coupon-btn">
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <div className="mb-3">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Tag size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                      <input value={couponInput}
                             onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                             placeholder="Coupon code"
                             className="w-full pl-7 pr-2 py-2 text-sm border border-zinc-200 rounded-lg uppercase"
                             data-testid="coupon-input" />
                    </div>
                    <button type="button" onClick={applyCoupon} disabled={couponLoading || !couponInput.trim()}
                            className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-zinc-900 text-white disabled:opacity-40"
                            data-testid="apply-coupon-btn">
                      {couponLoading ? "…" : "Apply"}
                    </button>
                  </div>
                  {couponError && <div className="text-xs text-red-600 mt-1" data-testid="coupon-error">{couponError}</div>}
                </div>
              )}
            </div>

            <div className="border-t border-zinc-200 pt-4 space-y-2 text-sm">
              <Row label="Subtotal" value={fmtJMD(subtotal)} />
              {discount > 0 && <Row label="Discount" value={`-${fmtJMD(discount)}`} accent="#10B981" />}
              <Row label={isPickup ? "Total (pickup)" : "Subtotal total"} value={fmtJMD(total)} bold />
            </div>
            <div className="mt-3 rounded-lg bg-zinc-50 border border-zinc-200 p-3 text-[11px] text-zinc-600 leading-relaxed"
                 data-testid="fee-notice">
              {isPickup ? (
                <>
                  <strong className="text-zinc-900">Pickup:</strong> no delivery fee. Our team will confirm your
                  pickup day &amp; time when we process your order.
                </>
              ) : (
                <>
                  <strong className="text-zinc-900">Delivery fee not included.</strong> A delivery fee based on your
                  location will be added by our team when we review the order — we&rsquo;ll message you the updated total and payment details.
                </>
              )}
            </div>
            <button type="submit" disabled={submitting} className="btn-primary w-full mt-5 disabled:opacity-50" data-testid="place-order-btn">
              {submitting ? "Placing…" : "Place order"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Input({ label, required, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-zinc-600 block mb-1">{label}{required && <span className="text-[#F74B64]"> *</span>}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
             className="w-full border border-zinc-200 rounded-lg px-3 py-2" data-testid={testid} />
    </div>
  );
}
function Row({ label, value, bold, accent }) {
  return (
    <div className={`flex justify-between ${bold ? "text-base font-bold" : "text-zinc-600"}`}
         style={accent ? { color: accent } : {}}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}
